import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import axios from "axios";

const baseUrl =
  process.env.REACT_APP_PUBLIC_API_URL || process.env.REACT_APP_API_URL;
const API_URL = `${baseUrl}/api`;

const initialFormData = {
  firstName: "",
  lastName: "",
  middleName: "",
  dateOfBirth: "",
  gender: "",
  ageGroup: "",
  idNumber: "",
  disability: "",
  region: "",
  district: "",
  community: "",
  phoneNumber: "",
  email: "",
  whatsapp: "",
  highestEducation: "",
  enrollmentStatus: "",
  completionStatus: "",
  mainOccupation: "",
  interestInAgriculture: "",
  valueChainInterest: "",
  currentEngagement: "",
  landAccess: "",
  financeAccess: "",
  toolsTechnologyAccess: "",
  mobilephoneAccess: "",
  safetyPerception: "",
  riskHarmful: "",
  reportIssues: "",
  safeguardinginfo: "",
};

const sections = [
  {
    title: "Personal Information",
    fields: [
      { name: "firstName", label: "First Name", type: "text", required: true },
      { name: "lastName", label: "Last Name", type: "text", required: true },
      {
        name: "middleName",
        label: "Middle Name",
        type: "text",
        required: false,
      },
      {
        name: "dateOfBirth",
        label: "Date of Birth",
        type: "date",
        required: true,
      },
      {
        name: "gender",
        label: "Gender",
        type: "select",
        required: true,
        options: ["Male", "Female", "Other", "Prefer not to say"],
      },
      {
        name: "ageGroup",
        label: "Age Group",
        type: "select",
        required: true,
        options: ["18-21", "22-26", "27-31", "32-35"],
      },
      {
        name: "idNumber",
        label: "Ghana Card Number",
        type: "text",
        required: false,
      },
      {
        name: "disability",
        label: "Disability Status",
        type: "select",
        required: false,
        options: [
          "No",
          "Yes - Mobility (difficulty walking, using hands)",
          "Yes - Visual impairment (blindness or low vision",
          "Yes - Hearing impairment",
          "Yes - Psychological disability/mental health",
          "Yes - Other",
        ],
      },
    ],
  },
  {
    title: "Contact Information",
    fields: [
      {
        name: "region",
        label: "Region",
        type: "select",
        required: true,
        options: ["Greater Accra", "Western", "Eastern", "Central", "Volta"],
      },
      {
        name: "district",
        label: "District",
        type: "select",
        required: true,
        options: [
          "Accra Metropolitan",
          "Ashaiman",
          "Asikuma/Odoben Brakwa",
          "Bawku",
          "Bolgatanga",
          "Bongo",
          "Bumtiri",
          "Chiraa",
          "Dagbon",
          "Denkyira",
          "Eastern",
          "Fanteakwa",
          "Fiave",
          "Ga Central",
          "Ga East",
          "Ga West",
          "Gomoa",
          "Gomoa East",
          "Gomoa West",
          "Greater Accra",
          "Huni-Valley",
          "Jaman South",
          "Kadjebi",
          "Keta",
          "Klottey Korle",
          "Lawra",
          "Lokoe",
          "Lower Manya Krobo",
          "Mampong",
          "Manya Krobo",
          "Nadowli/Kaleo",
          "Nangodi",
          "Navrongo",
          "Nkawkaw",
          "Nsawam/Akuapem",
          "Obuasi",
          "Oda",
          "Okaikwei",
          "Okere",
          "Okwahu",
          "Pru",
          "Sene",
          "Shama",
          "Shama Traditional",
          "Shia",
          "Sekondi-Takoradi",
          "Sekyere",
          "South Dayi",
          "South Tongu",
          "Suhum",
          "Sunyani",
          "Swedru",
          "Tarkwa",
          "Teshi",
          "Teshi East",
          "Teshi West",
          "Tumu",
          "Upper East",
          "Upper West",
          "Wa",
          "Wassa East",
          "Wassa West",
          "Wenchi",
          "Yendi",
          "Zabzugu",
        ],
      },
      {
        name: "community",
        label: "Community/Town",
        type: "text",
        required: true,
      },
      { name: "phoneNumber", label: "Phone", type: "tel", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      {
        name: "whatsapp",
        label: "Whatsapp Phone",
        type: "tel",
        required: false,
      },
    ],
  },
  {
    title: "Agriculture Interested & Engagement",
    fields: [
      {
        name: "interestInAgriculture",
        label: "Interest in Agriculture",
        type: "select",
        options: [
          "Yes - as a business / income-generating activity",
          "Yes - as a hobby/substinence (non-income)",
          "Yes - assisting parents/guardian",
          "No - not engaged",
        ],
      },
      {
        name: "valueChainInterest",
        label: "Value Chain Interest",
        type: "select",
        options: [
          "Vegetables",
          "Tree Crops",
          "Poultry Production",
          "Livestock Production",
          "Beekeeping",
          "Farm Planning and Management",
          "Agribusiness and Financial Management",
          "Marketing of Agric Produce",
          "Entrepreneurship",
          "Career & Counselling Guidance for SHS",
        ],
      },
      {
        name: "currentEngagement",
        label: "Already engaged in Agricultural Activities?",
        type: "select",
        options: [
          "Yes - As a business/income generating activity",
          "Yes - as a hobby/substinence (non-income)",
          "Yes - assisting parents/guardian",
          "No - not engaged",
        ],
      },
    ],
  },
  {
    title: "Education & Employment",
    fields: [
      {
        name: "highestEducation",
        label: "Highest Level of Education",
        type: "select",
        required: true,
        options: [
          "No formal education",
          "Primary",
          "JHS",
          "SHS",
          "TVET",
          "Diploma",
          "Bachelor's Degree",
          "Master's Degree",
          "PhD",
          "Other",
        ],
      },
      {
        name: "enrollmentStatus",
        label: "Are you currently enrolled in School, College or Training?",
        type: "select",
        required: false,
        options: ["Yes - full-time", "Yes - Part Time", "No"],
      },
      {
        name: "completionStatus",
        label: "Completion Status",
        type: "select",
        required: false,
        options: [
          "Yes - Completed and not Currently Studying",
          "No - Currently Studying and not yet Completed",
          "Not Applicable (Never Enrolled)",
        ],
      },
      {
        name: "mainOccupation",
        label: "Occupation",
        type: "select",
        required: false,
        options: [
          "Student",
          "Wage Employement (informal)",
          "Employment salary (formal)",
          "Unemployed / Seeking work",
          "Self-employed (non-agriculture)",
          "Apprentice/trainee",
        ],
      },
    ],
  },
  {
    title: "Access to Resources",
    fields: [
      {
        name: "landAccess",
        label: "Land Access",
        type: "select",
        required: true,
        options: [
          "Yes - Own Land",
          "Yes - Rented/Leased",
          "Yes - Family land (informal access)",
          "No Access to Land",
        ],
      },
      {
        name: "financeAccess",
        label: "Finance Access",
        type: "select",
        required: true,
        options: [
          "Yes - formal loan (Bank, Microfinance)",
          "Yes - Informal Loan or Savings Group",
          "No Access to Finance",
        ],
      },
      {
        name: "toolsTechnologyAccess",
        label: "Tools/Technology Access",
        type: "select",
        required: false,
        options: ["Yes - Own", "Yes - Borrow/Shared", "No"],
      },
      {
        name: "mobilephoneAccess",
        label: "Availability",
        type: "select",
        required: true,
        options: [
          "Yes - smartphone with internet access",
          "Yes - basic phone (call/text only)",
          "No phone",
        ],
      },
    ],
  },
  {
    title: "Safeguarding",
    fields: [
      {
        name: "safetyPerception",
        label:
          "Do you feel safe participating in community agriculture activities?",
        type: "select",
        required: false,
        options: ["Always", "Sometimes", "Rarely", "Never"],
      },
      {
        name: "riskHarmful",
        label: "Risks or Harmful Practices Hindering you?",
        type: "select",
        required: true,
        options: ["Yes", "No"],
      },
      {
        name: "reportIssues",
        label: "Who You Would Report Issues to",
        type: "select",
        required: true,
        options: [
          "Community Leader",
          "Project staff",
          "Familiy member",
          "No one",
          "Other",
        ],
      },
      {
        name: "safeguardinginfo",
        label: "Do You Want Safeguarding Info?",
        type: "select",
        required: false,
        options: ["Yes", "No"],
      },
    ],
  },
];

export default function RegistrationFormModal({ isOpen, onClose, onSuccess }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (!isOpen) {
      setCurrentSection(0);
      setErrors({});
      setFormData(initialFormData);
    }
  }, [isOpen]);

  const validateSection = (sectionIndex) => {
    const section = sections[sectionIndex];
    const newErrors = {};
    section.fields.forEach((field) => {
      if (field.required && !formData[field.name]?.trim()) {
        newErrors[field.name] = `${field.label} is required`;
      }
      if (field.type === "email" && formData[field.name]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.name])) {
          newErrors[field.name] = "Please enter a valid email address";
        }
      }
      if (field.type === "tel" && formData[field.name]) {
        const phoneRegex = /^[0-9+\-\s()]+$/;
        if (!phoneRegex.test(formData[field.name])) {
          newErrors[field.name] = "Please enter a valid phone number";
        }
      }
      if (field.name === "dateOfBirth" && formData[field.name]) {
        const birthDate = new Date(formData[field.name]);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const actualAge =
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
            ? age - 1
            : age;
        if (actualAge < 18 || actualAge > 35) {
          newErrors[field.name] = "You must be between 18 and 35 years old";
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    let sanitizedValue = value;
    if (typeof value === "string") {
      sanitizedValue = value
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
        .replace(/javascript:/gi, "")
        .replace(/on\w+\s*=/gi, "");
    }
    setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleNext = () => {
    if (
      validateSection(currentSection) &&
      currentSection < sections.length - 1
    ) {
      setCurrentSection((s) => s + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) setCurrentSection((s) => s - 1);
  };

  const submitForm = async () => {
    let allValid = true;
    for (let i = 0; i < sections.length; i++) {
      if (!validateSection(i)) {
        allValid = false;
        setCurrentSection(i);
        break;
      }
    }
    if (!allValid) return;

    setIsSubmitting(true);
    try {
      const sanitizedData = {};
      Object.keys(formData).forEach((key) => {
        let value = formData[key];
        if (typeof value === "string") {
          value = value
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
            .replace(/javascript:/gi, "")
            .replace(/on\w+\s*=/gi, "")
            .trim();
        }
        sanitizedData[key] = value;
      });

      const response = await axios.post(
        `${API_URL}/registrations`,
        sanitizedData,
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        },
      );

      const data = response.data;
      if (data.success) {
        if (typeof onSuccess === "function") onSuccess();
        onClose();
        return;
      }
      setErrors({ submit: data.message || "Registration failed." });
    } catch (error) {
      console.error("Registration error:", error);
      const status = error.response?.status;
      const message = error.response?.data?.message;
      if (status === 409) {
        setErrors({
          submit:
            "This email is already registered. Please use a different email address.",
          email: "This email is already in use.",
        });
      } else {
        setErrors({
          submit: message || "An error occurred. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.name] || "";
    const error = errors[field.name];
    const hasError = !!error;
    return (
      <div key={field.name} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {field.type === "select" ? (
          <select
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm ${hasError ? "border-red-500" : "border-gray-300"} bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent`}
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={field.type === "tel" ? "tel" : field.type}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm ${hasError ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-green-500 focus:border-transparent`}
            required={field.required}
          />
        )}
        {hasError && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">New registrant</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600">
            Section {currentSection + 1} of {sections.length}:{" "}
            {sections[currentSection].title}
          </p>
          <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-green-600 h-1.5 rounded-full transition-all duration-300"
              style={{
                width: `${((currentSection + 1) / sections.length) * 100}%`,
              }}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSection}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.2 }}
            >
              <h3 className="text-base font-semibold text-gray-800 mb-4">
                {sections[currentSection].title}
              </h3>
              <div className="space-y-2">
                {sections[currentSection].fields.map((f) => renderField(f))}
              </div>
            </motion.div>
          </AnimatePresence>
          {errors.submit && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          {currentSection > 0 ? (
            <button
              type="button"
              onClick={handlePrevious}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
            >
              <ArrowLeft size={18} />
              Previous
            </button>
          ) : (
            <div />
          )}
          <button
            type="button"
            onClick={
              currentSection === sections.length - 1 ? submitForm : handleNext
            }
            disabled={isSubmitting}
            className="ml-auto flex items-center gap-2 px-5 py-2 rounded-lg font-semibold text-white bg-green-700 hover:bg-green-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {currentSection === sections.length - 1
              ? isSubmitting
                ? "Submitting…"
                : "Submit"
              : "Next"}
            {currentSection < sections.length - 1 && <ArrowRight size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
